import { CreateMortgageLeadTassoCommand } from '@tassomutuo-backoffice/types';

import type { AcquisitionReviewSection } from '../acquisition/AcquisitionConfirmDialog';
import { acquisitionEuro } from '../acquisition/acquisitionAmount';
import { acquisitionLocationLabel } from '../acquisition/acquisitionCustomer';
import {
  LEAD_TASSO_PURPOSE_OPTIONS,
  LEAD_TASSO_RATE_TYPE_OPTIONS,
} from './leadTassoOptions';

export const LEAD_TASSO_REVIEW_TITLE = 'Conferma acquisizione Lead Tasso';

/**
 * Mutuo has no Account Area Clienti, so the review never mentions credentials:
 * the only side effect the operator chooses here is the first message.
 */
export const LEAD_TASSO_REVIEW_NOTICE =
  'Confermando, la lead viene registrata in Programma Tasso come Lead in gestione e presa in carico. L’operazione non è annullabile.';

const welcomeCopy = (enabled: boolean) =>
  enabled
    ? 'Al cliente verrà inviata la comunicazione di benvenuto. La successiva comunicazione sul consulente assegnato viene sempre inviata.'
    : 'Non verrà inviata alcuna comunicazione di benvenuto al cliente. La successiva comunicazione sul consulente assegnato viene sempre inviata.';

/**
 * The operator vocabulary for a command value. An enum the form cannot produce
 * falls back to the raw value rather than to a blank cell, so the review never
 * hides something the request carries.
 */
const optionLabel = (
  options: { value: string; label: string }[],
  value: string
) => options.find((option) => option.value === value)?.label ?? value;

/**
 * The read-only review of one compiled Mutuo draft. It reads the command that
 * confirmation will send, not the form behind it, so what the operator approves
 * and what leaves the browser cannot drift apart. Only the operator's own
 * choices appear: the acquisition origin, the owner, the consents and the
 * idempotency key of the submission are server-owned or header-only.
 */
export const leadTassoReviewSections = (
  command: CreateMortgageLeadTassoCommand
): AcquisitionReviewSection[] => [
  {
    title: 'Informazioni mutuo',
    fields: [
      {
        label: 'Finalità del mutuo',
        value: optionLabel(LEAD_TASSO_PURPOSE_OPTIONS, command.purpose),
      },
      { label: 'Durata', value: `${command.durationYears} anni` },
      { label: 'Valore immobile', value: acquisitionEuro(command.propertyValue) },
      { label: 'Importo richiesto', value: acquisitionEuro(command.loanAmount) },
      { label: 'Cliente under 36', value: command.under36 ? 'Sì' : 'No' },
      {
        label: 'Tasso preferito',
        value: optionLabel(LEAD_TASSO_RATE_TYPE_OPTIONS, command.rateType),
      },
    ],
  },
  {
    title: 'Informazioni cliente',
    fields: [
      { label: 'Nome', value: command.firstName },
      { label: 'Cognome', value: command.lastName },
      { label: 'Email', value: command.email },
      {
        label: 'Telefono',
        value: `${command.phonePrefix} ${command.phone}`,
      },
    ],
  },
  {
    title: 'Località',
    fields: [{ label: 'Località', value: acquisitionLocationLabel(command.location) }],
  },
  {
    title: 'Comunicazione',
    fields: [
      {
        label: 'Comunicazione di benvenuto',
        value: command.sendWelcomeCommunication ? 'Sì' : 'No',
      },
    ],
    note: welcomeCopy(command.sendWelcomeCommunication),
  },
];
